/**
 * SaveStatusContext
 *
 * Tracks in-flight mutations via a ref counter and exposes
 * derived save status for the toolbar indicator.
 */

import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import {
	SAVE_STATUS_DISPLAY_MS,
	SAVE_ERROR_DISPLAY_MS,
} from '@/constants/EditorConstants'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface SaveStatusContextValue {
	status: SaveStatus
	lastError: Error | null
	trackMutation: <T>(fn: () => Promise<T>) => Promise<T>
}

const SaveStatusContext = createContext<SaveStatusContextValue | null>(null)

interface SaveStatusProviderProps {
	children: ReactNode
}

export function SaveStatusProvider({ children }: SaveStatusProviderProps) {
	const [status, setStatus] = useState<SaveStatus>('idle')
	const [lastError, setLastError] = useState<Error | null>(null)
	const pendingRef = useRef(0)
	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const clearIdleTimer = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current)
			idleTimerRef.current = null
		}
	}, [])

	const trackMutation = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
		pendingRef.current++
		clearIdleTimer()
		setStatus('saving')

		try {
			const result = await fn()

			pendingRef.current--
			if (pendingRef.current === 0) {
				setStatus('saved')
				setLastError(null)
				idleTimerRef.current = setTimeout(() => {
					setStatus('idle')
				}, SAVE_STATUS_DISPLAY_MS)
			}

			return result
		} catch (err) {
			pendingRef.current--
			const error = err instanceof Error ? err : new Error(String(err))
			setStatus('error')
			setLastError(error)
			idleTimerRef.current = setTimeout(() => {
				setStatus('idle')
				setLastError(null)
			}, SAVE_ERROR_DISPLAY_MS)
			throw err
		}
	}, [clearIdleTimer])

	return (
		<SaveStatusContext.Provider value={{ status, lastError, trackMutation }}>
			{children}
		</SaveStatusContext.Provider>
	)
}

export function useSaveStatus(): SaveStatusContextValue {
	const ctx = useContext(SaveStatusContext)
	if (!ctx) {
		throw new Error('useSaveStatus must be used within a SaveStatusProvider')
	}
	return ctx
}
